import React, { useEffect, useState } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { observer, inject } from 'mobx-react';
import { delay } from 'helpers/AsyncHelper';
import { makeStyles } from '@mui/styles';
import { WizardModel, WizardStep } from 'models/kiosk/WizardModel';
import { Billpay } from 'models/kiosk/Billpay';
import { StoreModel } from 'models/Index';
import { PaymentBody, PaymentFooter } from 'components/payment';

import {
    CloseWarningModal,
    PaymentProcessFailedModal,
    NoChangeMessageModal,
} from '../modals';

import {
    CashReceivingRequest,
    CashReceivingStatus,
} from '@b2bsoft/kiosk-common-sdk';

import { Services, Timeouts, logger, LogEvents } from 'helpers';

import {
    goApplicationSelect,
    goHome,
    startProcessingCash,
} from 'sdk/InternalSdk';

interface CashAccepterStepProps extends WithTranslation { }
interface CashAccepterStepPropsInjected extends CashAccepterStepProps {
    Store: StoreModel;
    wizard: WizardModel;
    billpay: Billpay;
}

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        margin: 'calc(10vh/100*5) 10% calc(10vh/100*5) 10%',
    },
    underline: {
        marginTop: '40px',
        borderTop: '1px solid ',
    },
});

const CashAccepterStep = observer<React.FC<CashAccepterStepProps>>((props) => {
    const { billpay, wizard, Store, t } =
        props as CashAccepterStepPropsInjected;
    const classes = useStyles();
    const [noChangeModalOpen, setNoChangeModalOpen] = useState(false);
    const [warningModalOpen, setWarningModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const startReceivingCash = async (): Promise<any> => {
        logger.logInformation(
            'Starting CashReceiver.',
            LogEvents.CashReceiverStarted,
            {
                customer: billpay.customer,
                processInstanceId: billpay.processId
            }
        );

        const data = {
            appID: Store.paymentAttributes.appId,
            processId: String(billpay.processId),
        } as CashReceivingRequest;

        try {
            document.addEventListener(
                'BanknoteEscrowedEventRaised',
                billpay.onCashEscrowed
            );
            await startProcessingCash(Store, data, {
                timeout: Timeouts.SdkCashReceiving,
            });
            // reset error message
            setErrorMessage(null);
        } catch (error: any) {
            console.error('Error during processing cash %o', error);

            if (error.statusMessage) {
                setErrorMessage(t(error.statusMessage));
            }
            else if (error.message) {
                setErrorMessage(error.message);
            }

            // if cash receiving operation was cancelled since we went to next page
            if (error.status === CashReceivingStatus.Canceled) {
                return;
            }
            //We don't want to retry immediately, since it could be repeated error
            return delay(2000).then(() => startReceivingCash());
        }
    };

    const onNext = async () => {
        await Store.hideHomeButton();
        Store.shellClient.cashAcceptor.stopReceivingCash();
        return billpay
            .completeTransaction()
            .then(() => {
                if (billpay.isTransactionCreated) {
                    Store.showHomeButton();
                }
            })
            .finally(() => wizard.goToStep(WizardStep.PrintReceipt));
    };

    const onPrev = () => {
        goApplicationSelect();
    };

    const handleNoChangeModalClose = (
        event: {},
        reason: 'backdropClick' | 'escapeKeyDown'
    ) => {
        if (reason === 'backdropClick') {
            console.log(reason);
        } else {
            setNoChangeModalOpen(false);
        }
    };

    useEffect(() => {
        let isMounted = true;
        const createTransactionAndOpenModal = async () => {
            await billpay.createTransaction();
        };

        if (isMounted) {
            setNoChangeModalOpen(true);
        }
        createTransactionAndOpenModal();

        return () => {
            isMounted = false;
            Store.shellClient.cashAcceptor.stopReceivingCash();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={classes.root}>
            <PaymentBody billpay={billpay} errorMessage={errorMessage} />
            <PaymentFooter
                onNext={onNext}
                onPrev={onPrev}
                hasInsertedCash={billpay.hasInsertedCash}
            />
            <NoChangeMessageModal
                modalProps={{
                    open: noChangeModalOpen,
                    disableEscapeKeyDown: true,
                    onClose: handleNoChangeModalClose,
                }}
                buttonProps={{
                    onClick: () => {
                        startReceivingCash();
                        setNoChangeModalOpen(false);
                    },
                }}
            />
            <CloseWarningModal
                modalProps={{
                    open: warningModalOpen,
                    onClose: () => {
                        setWarningModalOpen(false);
                    },
                }}
                buttonProps={{
                    onClick: () => {
                        setWarningModalOpen(false);
                    },
                }}
            />
            {billpay.processFailed && !billpay.hasInsertedCash && (
                <PaymentProcessFailedModal
                    modalProps={{
                        open: true,
                        onClose: () => goHome(),
                    }}
                    buttonProps={{
                        onClick: () => goHome(),
                    }}
                />
            )}
        </div>
    );
});

export default inject(
    Services.STORE,
    Services.PAYMENT_WIZARD,
    Services.BILLPAY
)(observer(withTranslation()(CashAccepterStep)));
